import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Prism } from './Prism';

interface Props {
  onSubmit: (text: string) => void | Promise<void>;
  disabled?: boolean;
  agentSlug: string;
  /**
   * Phase 7e+: when set and the composer is disabled (a turn is pending),
   * the send button is replaced by a Cancel button. Clicking it aborts
   * the in-flight POST; the worker catches the AbortError between
   * Anthropic round trips and persists a "turn cancelled" agent message.
   */
  onCancel?: () => void;
}

const COLLAPSED_HEIGHT = 56;
const LINE_HEIGHT_PX = 22; // 15 px * 1.5
const MAX_LINES = 5;
const MAX_LINES_PX = LINE_HEIGHT_PX * MAX_LINES;

/**
 * Composer.
 *
 * Per docs/DESIGN-DECISIONS.md §1.5.
 *
 * Phase C2 collapsed: 56 px tall.
 * Phase C6 expanded: textarea auto-grows from 56 px up to MAX_LINES (5)
 * lines, capped at 50 % of viewport. Never expands when input is empty.
 *
 * Send button: 44 x 44 visual inside 56 px tap area.
 *   - Has content: active agent's Prism at 20 px.
 *   - Empty: paper-plane outline at 20 px.
 */
export function Composer({ onSubmit, disabled, agentSlug, onCancel }: Props) {
  const [text, setText] = useState('');
  const taRef = useRef<HTMLTextAreaElement | null>(null);
  const [taHeight, setTaHeight] = useState(LINE_HEIGHT_PX); // single-line at first

  useLayoutEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    if (text.length === 0) {
      setTaHeight(LINE_HEIGHT_PX);
      ta.style.height = `${LINE_HEIGHT_PX}px`;
      return;
    }
    ta.style.height = '0px';
    const measured = ta.scrollHeight;
    const maxByViewport = Math.floor(window.innerHeight * 0.5) - COLLAPSED_HEIGHT;
    const cap = Math.min(MAX_LINES_PX, Math.max(LINE_HEIGHT_PX, maxByViewport));
    const target = Math.min(measured, cap);
    setTaHeight(target);
    ta.style.height = `${target}px`;
  }, [text]);

  // Recompute on viewport resize (keyboard open/close on mobile triggers this).
  useEffect(() => {
    function onResize() {
      const ta = taRef.current;
      if (!ta || text.length === 0) return;
      ta.style.height = '0px';
      const measured = ta.scrollHeight;
      const maxByViewport = Math.floor(window.innerHeight * 0.5) - COLLAPSED_HEIGHT;
      const cap = Math.min(MAX_LINES_PX, Math.max(LINE_HEIGHT_PX, maxByViewport));
      const target = Math.min(measured, cap);
      setTaHeight(target);
      ta.style.height = `${target}px`;
    }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [text]);

  async function fire() {
    const t = text.trim();
    if (!t || disabled) return;
    setText('');
    await onSubmit(t);
    taRef.current?.focus();
  }

  const hasContent = text.trim().length > 0;
  const expandedExtra = Math.max(0, taHeight - LINE_HEIGHT_PX);
  const containerHeight = COLLAPSED_HEIGHT + expandedExtra;

  return (
    <div
      className="sticky bottom-0 w-full bg-night-900 border-t border-night-600 flex-shrink-0"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <div
        className="flex items-end pl-4 pr-2 gap-2"
        style={{ height: `${containerHeight}px`, paddingTop: 8, paddingBottom: 8 }}
      >
        <textarea
          ref={taRef}
          rows={1}
          className="flex-1 bg-transparent border-0 px-0 py-0 resize-none
                     text-[15px] text-paper-100 placeholder:text-night-500
                     focus:outline-none font-sans leading-[1.5]
                     overflow-y-auto self-end"
          style={{ height: taHeight }}
          placeholder="Write to the agent."
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            // Phase 7b decision (refined Phase 11): Cmd/Ctrl+Enter sends at
            // every breakpoint. Plain Enter always inserts a newline. The
            // §1.5 spec originally varied this by breakpoint, but two
            // reviewers flagged the breakpoint conditional as confusing
            // ergonomics. Touch keyboards still send via the OS Send key
            // (submit, not keydown), so phone/tablet UX is unchanged.
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              fire();
            }
          }}
          disabled={disabled}
        />
        {disabled && onCancel ? (
          <button
            type="button"
            onClick={onCancel}
            aria-label="Cancel turn"
            title="Cancel turn"
            className="w-14 h-14 flex items-center justify-center self-end flex-shrink-0
                       focus:outline-none transition-colors rounded-md
                       text-paper-300 hover:text-paper-100 hover:bg-night-800"
          >
            <CancelGlyph size={20} />
          </button>
        ) : (
          <button
            type="button"
            onClick={fire}
            disabled={disabled || !hasContent}
            aria-label="Send"
            className="w-14 h-14 flex items-center justify-center self-end flex-shrink-0
                       disabled:opacity-40 disabled:cursor-not-allowed
                       focus:outline-none transition-colors rounded-md
                       hover:bg-night-800"
          >
            {hasContent ? (
              <Prism slug={agentSlug} size={20} active />
            ) : (
              <PaperPlane size={20} />
            )}
          </button>
        )}
      </div>
    </div>
  );
}

function CancelGlyph({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden="true">
      <rect x="3" y="3" width="10" height="10" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

function PaperPlane({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      aria-hidden="true"
      className="text-paper-300"
    >
      <path
        d="M14 2 L1 7.2 L6 9 L8 14 L14 2 Z M6 9 L14 2"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
